import { Avatar, Card, CardHeader, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import AdminServiceStatusCard from '../../components/admin/subscriberEdit/AdminServiceStatusCard';
import AdminSubscriberCreateCard from '../../components/admin/subscriberEdit/AdminSubscriberCreateCard';
import AdminSubscriberEditSectionCard, {
    AdminEditableField,
    AdminSectionValues,
} from '../../components/admin/subscriberEdit/AdminSubscriberEditSectionCard';
import BackNavigationButton from '../../components/common/BackNavigationButton';
import PageContainer from '../../components/common/PageContainter';
import useSubscriberAdminDetails, {
    AdminEditableServiceName,
    AdminSubscriberDetailsUpdate,
} from '../../hooks/useSubscriberAdminDetails';

const ACCOUNT_FIELDS: AdminEditableField[] = [
    { key: 'username', label: 'OnlyFans Username', required: true },
    { key: 'cognito.username', label: 'Cognito Username', helperText: 'Dashboard login this subscriber belongs to' },
    { key: 'email', label: 'OnlyFans Email', type: 'email', required: true },
    { key: 'password', label: 'OnlyFans Password', type: 'password', required: true },
];

const CONTACT_FIELDS: AdminEditableField[] = [
    { key: 'contactEmail', label: 'Contact Email', type: 'email' },
    { key: 'telegramChatId', label: 'Telegram Chat ID', required: true },
    { key: 'realName', label: 'Real Name' },
];

const FINANCIAL_FIELDS: AdminEditableField[] = [
    { key: 'financial.billingName', label: 'Billing Name', gridWidth: 6 },
    { key: 'financial.billingCompanyName', label: 'Company Name', gridWidth: 6 },
    { key: 'financial.billingAddress', label: 'Address', gridWidth: 12 },
    { key: 'financial.billingCity', label: 'City', gridWidth: 6 },
    { key: 'financial.billingCounty', label: 'County / State', gridWidth: 6 },
    { key: 'financial.billingPostcode', label: 'Postcode', gridWidth: 6 },
    { key: 'financial.billingCountry', label: 'Country', gridWidth: 6 },
    { key: 'financial.billingVatNumber', label: 'VAT Number', gridWidth: 6 },
];

const GENERAL_FIELDS: AdminEditableField[] = [
    { key: 'general.hasLeftReview', label: 'Has Left a Review', type: 'switch' },
    { key: 'general.doNotContact', label: 'Do Not Contact', type: 'switch' },
];

const NOTES_FIELDS: AdminEditableField[] = [{ key: 'notes', label: 'Notes', type: 'multiline' }];

const SERVICES: { key: AdminEditableServiceName; title: string; description: string }[] = [
    { key: 'sextforce', title: 'Sextforce', description: 'Sales tracking, metrics and automation suite' },
    { key: 'sextforceLimited', title: 'Sextforce Limited', description: 'Limited feature set of the Sextforce suite' },
    { key: 'housekeeping', title: 'Housekeeping', description: 'Automatic fans list management' },
    { key: 'renameUsers', title: 'Rename Users', description: 'Automatic renaming of fans' },
    { key: 'inCommonFans', title: 'In-Common Fans', description: 'Find fans shared with a second account' },
    { key: 'blockUsers', title: 'Block Users', description: 'Automatic blocking of users on a target list' },
    { key: 'unfollowUsers', title: 'Unfollow Users', description: 'Automatic unfollowing of users' },
];

/** Expands { "financial.billingName": "x" } into { financial: { billingName: "x" } } */
const expandDottedKeys = (flat: AdminSectionValues): AdminSubscriberDetailsUpdate => {
    const result: Record<string, any> = {};

    Object.entries(flat).forEach(([key, value]) => {
        const parts = key.split('.');
        let target = result;

        parts.slice(0, -1).forEach(part => {
            target[part] = target[part] || {};
            target = target[part];
        });

        target[parts[parts.length - 1]] = value;
    });

    return result;
};

/**
 * Admin-only page for editing a subscriber's account details and the paid
 * status of their services. Each section saves independently and only the
 * fields that were actually changed are sent to the server.
 */
const AdminSubscriberEdit = () => {
    const params = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const {
        data: details,
        isLoading,
        isFetching,
        updateDetails,
        updateService,
    } = useSubscriberAdminDetails(params.userId);

    const showSkeleton: boolean = isLoading || (isFetching && !details);

    const detailsValues: AdminSectionValues = useMemo(
        () => ({
            username: details?.username ?? '',
            'cognito.username': details?.cognito?.username ?? '',
            email: details?.email ?? '',
            password: details?.password ?? '',
            contactEmail: details?.contactEmail ?? '',
            telegramChatId: details?.telegramChatId ?? '',
            realName: details?.realName ?? '',
            notes: details?.notes ?? '',
            'financial.billingName': details?.financial?.billingName ?? '',
            'financial.billingCompanyName': details?.financial?.billingCompanyName ?? '',
            'financial.billingAddress': details?.financial?.billingAddress ?? '',
            'financial.billingCity': details?.financial?.billingCity ?? '',
            'financial.billingCounty': details?.financial?.billingCounty ?? '',
            'financial.billingPostcode': details?.financial?.billingPostcode ?? '',
            'financial.billingCountry': details?.financial?.billingCountry ?? '',
            'financial.billingVatNumber': details?.financial?.billingVatNumber ?? '',
            'general.hasLeftReview': details?.general?.hasLeftReview === true,
            'general.doNotContact': details?.general?.doNotContact === true,
        }),
        [details],
    );

    const handleSaveDetails = async (changed: AdminSectionValues): Promise<boolean> => {
        const result = await updateDetails(expandDottedKeys(changed));

        if (result) {
            enqueueSnackbar('Subscriber details updated', { variant: 'success' });
        }

        return result !== null;
    };

    const handleSaveService = async (
        service: AdminEditableServiceName,
        update: { active?: boolean; paidDate?: string | null },
    ): Promise<boolean> => {
        const result = await updateService(service, update);

        if (result) {
            enqueueSnackbar(`${SERVICES.find(s => s.key === service)?.title || service} updated`, { variant: 'success' });
        }

        return result !== null;
    };

    // Create mode - no subscriber ID in the URL
    if (!params.userId) {
        return (
            <PageContainer maxWidth="md">
                <Stack direction="row" alignItems="center" spacing={1} sx={{ marginBottom: 2 }}>
                    <BackNavigationButton />
                    <Typography variant="h5">Add Subscriber</Typography>
                </Stack>
                <AdminSubscriberCreateCard />
            </PageContainer>
        );
    }

    return (
        <PageContainer maxWidth="lg">
            <Stack direction="row" alignItems="center" spacing={1} sx={{ marginBottom: 2 }}>
                <BackNavigationButton />
                <Typography variant="h5">Edit Subscriber</Typography>
            </Stack>

            <Card variant="elevation" sx={{ marginBottom: 2 }}>
                <CardHeader
                    avatar={
                        showSkeleton ? (
                            <Skeleton variant="circular" width={40} height={40} />
                        ) : (
                            <Avatar>{(details?.username || '?').charAt(0).toUpperCase()}</Avatar>
                        )
                    }
                    title={showSkeleton ? <Skeleton width={200} /> : details?.username}
                    titleTypographyProps={{ variant: 'h6' }}
                    subheader={showSkeleton ? <Skeleton width={260} /> : details?._id}
                />
            </Card>

            <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={12} md={6}>
                    <AdminSubscriberEditSectionCard
                        title="Account"
                        fields={ACCOUNT_FIELDS}
                        values={detailsValues}
                        isLoading={showSkeleton}
                        onSave={handleSaveDetails}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <AdminSubscriberEditSectionCard
                        title="Contact"
                        fields={CONTACT_FIELDS}
                        values={detailsValues}
                        isLoading={showSkeleton}
                        onSave={handleSaveDetails}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <AdminSubscriberEditSectionCard
                        title="Financial"
                        fields={FINANCIAL_FIELDS}
                        values={detailsValues}
                        isLoading={showSkeleton}
                        onSave={handleSaveDetails}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Stack spacing={2} sx={{ height: '100%' }}>
                        <AdminSubscriberEditSectionCard
                            title="General"
                            fields={GENERAL_FIELDS}
                            values={detailsValues}
                            isLoading={showSkeleton}
                            onSave={handleSaveDetails}
                        />
                        <AdminSubscriberEditSectionCard
                            title="Notes"
                            fields={NOTES_FIELDS}
                            values={detailsValues}
                            isLoading={showSkeleton}
                            onSave={handleSaveDetails}
                        />
                    </Stack>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ marginTop: 1 }}>
                        Services
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Each service's active status and paid date can be updated individually.
                    </Typography>
                </Grid>

                {SERVICES.map(service => (
                    <Grid item xs={12} sm={6} lg={4} key={service.key}>
                        <AdminServiceStatusCard
                            title={service.title}
                            description={service.description}
                            service={service.key}
                            status={details?.[service.key]}
                            isLoading={showSkeleton}
                            onSave={handleSaveService}
                        />
                    </Grid>
                ))}
            </Grid>
        </PageContainer>
    );
};

export default AdminSubscriberEdit;
